import { Controller } from '@hotwired/stimulus';

/**
 * Auto Submit Controller
 *
 * Automatically submits a form when inputs change. Supports two modes:
 * - Immediate submission via `submit()` (for selects, checkboxes, filter tabs)
 * - Debounced submission via `search()` (for text inputs with 300ms delay)
 *
 * Works on both `<form>` elements (uses requestSubmit) and non-form elements
 * like `<div>` (uses Turbo.visit with frame targeting). The formless mode
 * avoids nested `<form>` issues when the search is inside another form.
 *
 * When an input target is present (search forms), the controller preserves
 * focus and cursor position across Turbo Drive page renders. This prevents
 * the search input from losing focus when the page is replaced after a GET
 * form submission.
 *
 * Focus restoration uses a module-level variable rather than instance state,
 * because Turbo Drive replaces the DOM (disconnecting the old controller)
 * before rendering the new page (connecting the new controller).
 *
 * Targets:
 * - input: (optional) The search input field whose focus/cursor should be
 *          restored after Turbo renders the new page
 *
 * Values:
 * - url: (optional) Base URL for formless mode (Turbo.visit)
 * - frame: (optional) Turbo Frame ID for formless mode
 *
 * Actions:
 * - submit: Immediately submits the form
 * - search: Submits the form after a 300ms debounce
 */

// Module-level state survives controller disconnect/reconnect across Turbo navigations
let pendingFocusRestore = null;

export default class extends Controller {
  static targets = ['input'];
  static values = { url: String, frame: String };

  connect() {
    this.searchTimeout = null;

    // Restore focus if a previous controller instance saved cursor state before Turbo navigation
    if (pendingFocusRestore && this.hasInputTarget) {
      const { id, selectionStart, selectionEnd } = pendingFocusRestore;
      pendingFocusRestore = null;

      if (id && this.inputTarget.id === id) {
        this.inputTarget.focus();

        if (
          typeof this.inputTarget.setSelectionRange === 'function' &&
          ['text', 'search', 'url', 'tel', 'password'].includes(this.inputTarget.type)
        ) {
          this.inputTarget.setSelectionRange(selectionStart, selectionEnd);
        }
      }
    }
  }

  disconnect() {
    clearTimeout(this.searchTimeout);
  }

  submit() {
    this.#saveCursorState();

    if (this.element.tagName === 'FORM') {
      this.element.requestSubmit();
    } else {
      this.#visitFrame();
    }
  }

  search() {
    clearTimeout(this.searchTimeout);
    // 300ms matches the docblock advertisement on line 8 and "feels instant"
    // for the typical "I typed a few letters and stopped" search flow. 1000ms
    // was perceived as broken because the input feels unresponsive.
    this.searchTimeout = setTimeout(() => this.submit(), 300);
  }

  /**
   * Formless mode: builds URL from input values and navigates the Turbo Frame.
   * Used when the controller is on a <div> instead of a <form> to avoid nesting.
   * Sets the frame's src attribute directly to trigger a Turbo Frame fetch.
   */
  #visitFrame() {
    if (!this.hasUrlValue || !this.hasFrameValue) return;

    const url = new URL(this.urlValue, window.location.origin);

    if (this.hasInputTarget && this.inputTarget.value) {
      url.searchParams.set(this.inputTarget.name, this.inputTarget.value);
    }

    const frame = document.getElementById(this.frameValue);
    if (frame) {
      frame.src = url.toString();
    }
  }

  /**
   * Saves cursor state to the module-level variable so the next controller
   * instance (after Turbo replaces the DOM) can restore focus in connect().
   */
  #saveCursorState() {
    if (!this.hasInputTarget) return;

    const input = this.inputTarget;
    pendingFocusRestore = {
      id: input.id,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd,
    };
  }
}
